import { Group, Stack, Checkbox, Tooltip, ActionIcon } from "@mantine/core";
import {
  useCurrentUserLevel,
  useLevelConfigs,
} from "@/store/selectors/userProfileSelectors";
import { Text } from "@/components/ui/Text";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { IconArrowRight, IconInfoCircle } from "@tabler/icons-react";
import { useStyles } from "@/styles/useStyles";
import { useCallback } from "react";
import { Navigation } from "@/services/Navigation";

export function UserProfileLevels() {
  const levelConfigs = useLevelConfigs();
  const currentUserLevel = useCurrentUserLevel();
  const { theme } = useStyles();

  const scrollToSection = useCallback((sectionId: string) => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Find the scroll container by looking for a scrollable parent
        // The UserProfile component uses a Stack with overflow-y: auto
        let scrollContainer: HTMLElement | null = element.parentElement;
        while (scrollContainer) {
          const style = window.getComputedStyle(scrollContainer);
          if (
            (style.overflowY === "auto" || style.overflowY === "scroll") &&
            scrollContainer.scrollHeight > scrollContainer.clientHeight
          ) {
            break;
          }
          scrollContainer = scrollContainer.parentElement;
        }

        if (scrollContainer) {
          // Calculate offset relative to the scroll container
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const stickyTabs = document.querySelector('[ref*="stickyTabsRef"]') as HTMLElement;
          const stickyHeaderOffset = stickyTabs ? stickyTabs.offsetHeight : 0;
          
          // Calculate the scroll position
          const scrollOffset =
            elementRect.top -
            containerRect.top +
            scrollContainer.scrollTop -
            stickyHeaderOffset -
            50; // 50px padding

          scrollContainer.scrollTo({
            top: Math.max(0, scrollOffset),
            behavior: "smooth",
          });
        } else {
          // Fallback to window scrolling
          const stickyTabs = document.querySelector('[ref*="stickyTabsRef"]') as HTMLElement;
          const stickyHeaderOffset = stickyTabs ? stickyTabs.offsetHeight : 0;
          const scrollOffset = element.offsetTop - stickyHeaderOffset - 50;

          window.scrollTo({
            top: Math.max(0, scrollOffset),
            behavior: "smooth",
          });
        }
      }
    }, 150);
  }, []);

  const handleButtonClick = useCallback(
    (buttonHref: string, event?: any) => {
      // Check if this is a profile page URL with a hash
      if (buttonHref.startsWith("/profile#")) {
        if (event && typeof event.preventDefault === "function") {
          event.preventDefault();
        }
        const hash = buttonHref.split("#")[1];
        if (hash) {
          // Update the hash in the URL
          window.location.hash = hash;
          // Navigate to update React Router state
          Navigation.navigate(buttonHref);
          // Scroll to the section
          scrollToSection(hash);
        }
      }
      // For other URLs, let the Button component handle navigation normally
    },
    [scrollToSection],
  );

  return (
    <Stack gap="xs" maw={700}>
      {levelConfigs.map((level) => {
        const isCompleted = currentUserLevel
          ? currentUserLevel.level > level.level
          : true;
        const isActive = currentUserLevel?.level === level.level;

        return (
          <Group
            key={level.level}
            gap="sm"
            p="lg"
            justify="space-between"
            style={{
              backgroundColor: isActive
                ? theme.colors.primary[0]
                : "transparent",
              borderRadius: theme.radius.md,
              border: isActive
                ? `1px solid ${theme.colors.primary[2]}`
                : "none",
            }}
          >
            <Group gap="md" align="center" wrap="nowrap">
              <Checkbox
                color="primary"
                size="lg"
                readOnly
                checked={isCompleted}
              />
              <Badge
                color={isCompleted ? "primary" : isActive ? "primary" : "gray"}
                variant={isCompleted ? "light" : isActive ? "light" : "light"}
                ariaLabel={`Level ${level.level}`}
                size="lg"
                radius="xl"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                }}
              >
                Level {level.level}
              </Badge>

              <Group gap="xs" align="center" wrap="nowrap" style={{ flex: 1 }}>
                <Text fw={600} titleStyle="h4" size="md">
                  {level.title}
                  {isActive && currentUserLevel?.statusString && (
                    <>
                      {" "}
                      <span
                        style={{
                          fontSize: "0.9rem",
                          color: theme.colors.gray[7],
                          fontWeight: 400,
                        }}
                      >
                        ({currentUserLevel.statusString})
                      </span>
                    </>
                  )}
                </Text>
                {level.tooltip && (
                  <Tooltip
                    label={level.tooltip}
                    events={{ hover: true, focus: true, touch: true }}
                    position="top"
                    withArrow
                  >
                    <ActionIcon
                      variant="transparent"
                      size="sm"
                      color="gray"
                      aria-label="More information"
                    >
                      <IconInfoCircle size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>

            {isActive && level.buttonText && level.buttonHref && (
              <Button
                variant="filled"
                to={level.buttonHref}
                size="xs"
                color="primary"
                radius="xl"
                rightSection={<IconArrowRight size={16} />}
                style={{ alignSelf: "flex-start" }}
                onClick={(e: any) => {
                  if (level.buttonHref) {
                    handleButtonClick(level.buttonHref, e);
                  }
                }}
              >
                {level.buttonText}
              </Button>
            )}
          </Group>
        );
      })}
    </Stack>
  );
}
